import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { User } from '@supabase/supabase-js';
import storage from 'local-storage-fallback';

import { useUserInfo } from '../hooks/useUserInfo';
import { CharacterCreationSuccessDialog } from '../components/CharacterCreationSuccessDialog';
import { supabase } from '../lib/supabase';
import { EhUpgradeDialog } from '../components/EhUpgradeDialog';
import EhAuthDialog from '@/components/EhAuthDialog';
import { EhGemsDialog } from '@/components/EhGemsDialog';
import type { Database } from '../types/database';
import { getMediaUrl } from '@/lib/utils';
import { isFromAppStores, isHidingMonetizationInStores } from '@/lib/detection';
import { useTracking, UI_EVENTS, CHARACTER_EVENTS } from '@/hooks/useTracking';
import { Button } from '@/components/ui/button';
import { useGemBalance } from '@/hooks/useGemBalance';
import { SEO } from '@/components/SEO';
import { VoiceSelectionGrid } from '@/components/VoiceSelectionGrid';
import {
  LS_NEW_CHAR_KEY,
  VOICE_OPTIONS,
  GENDER_OPTIONS,
  STYLE_OPTIONS,
  ETHNICITY_OPTIONS,
  AGE_OPTIONS,
  EYE_COLOR_OPTIONS,
  HAIR_STYLE_OPTIONS,
  HAIR_COLOR_OPTIONS,
  BODY_TYPE_OPTIONS,
  BUST_SIZE_OPTIONS,
  BUTT_SIZE_OPTIONS,
  PERSONALITY_OPTIONS,
  RELATIONSHIP_OPTIONS,
  OCCUPATION_OPTIONS,
  HOBBY_OPTIONS,
  OUTFIT_OPTIONS
} from '@/lib/constants';
import type { OptionsType } from '@/lib/constants';

const TOTAL_STEPS = 8;
const CUSTOM_CHAR_COST = 10;
const FREE_CHAR_LIMIT = 1;
const FREE_PROFILE_IMAGE_LIMIT = 2;

const stepTitles = [
  'Gender & Style',
  'Ethnicity & Age',
  'Eyes & Hair',
  'Body',
  'Personality & Relationship',
  'Occupation & Hobbies',
  'Outfit & Voice',
  'Review',
];

const optionsMap: Record<string, OptionsType[]> = {
  style: STYLE_OPTIONS,
  ethnicity: ETHNICITY_OPTIONS,
  age: AGE_OPTIONS,
  eye_color: EYE_COLOR_OPTIONS,
  hair_style: HAIR_STYLE_OPTIONS,
  hair_color: HAIR_COLOR_OPTIONS,
  body_type: BODY_TYPE_OPTIONS,
  bust_size: BUST_SIZE_OPTIONS,
  butt_size: BUTT_SIZE_OPTIONS,
  personality: PERSONALITY_OPTIONS,
  relationship: RELATIONSHIP_OPTIONS,
  occupation: OCCUPATION_OPTIONS,
  hobbies: HOBBY_OPTIONS,
  outfit: OUTFIT_OPTIONS,
  voice: VOICE_OPTIONS,
}

interface CharacterState {
  current_step: number;
  updated_at: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attributes: Record<string, any>;
};

function getInitialState(): CharacterState {
  if (typeof window !== 'undefined') {
    const saved = storage.getItem(LS_NEW_CHAR_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        if (import.meta.env.DEV) console.log('Failed to load character state from localStorage', saved);
      }
    }
  }
  return {
    current_step: 0,
    updated_at: Date.now(),
    attributes: {},
  };
}

export default function CreateCharacterPage() {
  const navigate = useNavigate();
  const { characters, is18Confirmed, isSubscriber, isLoading: isLoadingUserInfo, refetchCharacters, user } = useUserInfo();
  const { balance, refreshBalance } = useGemBalance();
  const { trackEvent } = useTracking();

  const [state, setState] = useState<CharacterState>(getInitialState());
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [showPersonalityCustom, setShowPersonalityCustom] = useState(false);
  const [showRelationshipCustom, setShowRelationshipCustom] = useState(false);
  const [showOccupationCustom, setShowOccupationCustom] = useState(false);
  const [showHobbiesCustom, setShowHobbiesCustom] = useState(false);
  const [showOutfitCustom, setShowOutfitCustom] = useState(false);
  const [personalityCustom, setPersonalityCustom] = useState('');
  const [relationshipCustom, setRelationshipCustom] = useState('');
  const [occupationCustom, setOccupationCustom] = useState('');
  const [hobbiesCustom, setHobbiesCustom] = useState('');
  const [outfitCustom, setOutfitCustom] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSummaryDialog, setShowSummaryDialog] = useState(false);
  const [showGemsDialog, setShowGemsDialog] = useState(false);
  const [characterCreationError, setCharacterCreationError] = useState('');
  const [createdCharacter, setCreatedCharacter] = useState<Database['eh']['Tables']['characters']['Row'] | null>(null);
  const [imageStatus, setImageStatus] = useState<'idle'|'pending'|'success'|'failed'>('idle');
  const [avatarUrl, setAvatarUrl] = useState<string|null>(null);
  // const [imageRequestId, setImageRequestId] = useState<string|null>(null);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [showGemBalanceMsg, setShowGemBalanceMsg] = useState(false);

  const imageRetryCountRef = useRef(0);
  const imageRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const authDialogOnCloseHandlerRef = useRef<((open: boolean, { user, onboardingCompleted }: {
    user?: User,
    onboardingCompleted?: boolean
  }) => void)>((open, _data) => { setShowAuthDialog(open); });

  // Sync to localStorage
  useEffect(() => {
    storage.setItem(LS_NEW_CHAR_KEY, JSON.stringify(state));
  }, [state]);

  // Step navigation
  const goToStep = (step: number) => {
    // stopAllVoiceSamples(); // Stop any playing voice samples, now in dedicated VoiceSelectionGrid component
    setState((s: CharacterState) => ({ ...s, current_step: step, updated_at: Date.now() }));
    if (titleRef.current) {
      // give some time for step content to render
      setTimeout(() => titleRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  };
  const nextStep = () => goToStep(Math.min(state.current_step + 1, TOTAL_STEPS - 1));
  const prevStep = () => goToStep(Math.max(state.current_step - 1, 0));

  // --- Step 1: Gender & Style ---
  const handleSelect = (key: string, value: string | string[]) => {
    setState(s => {
      // If gender changes, reset all attributes except gender
      if (key === 'gender' && s.attributes.gender !== value) {
        const newGender = value as string;
        return {
          ...s,
          attributes: { // reset all attribute options incompatible with new gender, except hobbies
            ...Object.fromEntries(
              Object.entries(s.attributes).filter(
                ([k, v]) => k === 'hobbies'
                  || optionsMap[k] && genderFilterWithNewGender(optionsMap[k].find(o => o.value === v) || { missing: true }, newGender)
              )
            ),
            gender: newGender
          },
          updated_at: Date.now(),
        };
      }
      return {
        ...s,
        attributes: { ...s.attributes, [key]: value },
        updated_at: Date.now(),
      };
    });
  };

  const appStoreFilter = useCallback((opt: { notSafeForStores?: boolean }) => {
    return isFromAppStores() ? !opt.notSafeForStores : true;
  }, []);
  const genderFilter = (opt: { mOnly?: boolean, fOnly?: boolean }) => {
    return (opt.fOnly && gender === 'f')
      || (opt.mOnly && gender === 'm')
      || (!opt.fOnly && !opt.mOnly);
  };
  const genderFilterWithNewGender = (opt: { missing?: boolean, mOnly?: boolean, fOnly?: boolean }, newGender: string) => {
    // if (import.meta.env.DEV) console.log('[DEV] genderFilterWithNewGender', opt, newGender);
    return (opt.fOnly && newGender === 'f')
      || (opt.mOnly && newGender === 'm')
      || (!opt.missing && !opt.fOnly && !opt.mOnly);
  };

  const gender = state.attributes.gender;
  const style = state.attributes.style;
  const ethnicity = state.attributes.ethnicity;
  const age = state.attributes.age;
  const body_type = state.attributes.body_type;
  const bust_size = state.attributes.bust_size;
  const butt_size = state.attributes.butt_size;
  const personality = state.attributes.personality;
  const relationship = state.attributes.relationship;
  const occupation = state.attributes.occupation;
  const hobbies = state.attributes.hobbies || [];
  const outfit = state.attributes.outfit;
  const voice = state.attributes.voice;

  // Timeline UI
  const renderTimeline = () => (
    <div className={`max-w-full flex items-center justify-center gap-2 p-1 mb-2 overflow-hidden select-none ${isSubmitting ? 'hidden' : ''}`}>
      {stepTitles.map((title, idx) => (
        <button
          key={title}
          className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white transition-all ${
            idx === state.current_step
              ? 'bg-pink-600 scale-110' : idx < state.current_step
              ? 'bg-pink-400' : 'bg-zinc-700'
          }`}
          onClick={() => idx <= state.current_step && goToStep(idx)}
          disabled={idx > state.current_step}
        >
          {idx + 1}
        </button>
      ))}
    </div>
  );

  // Step 1 UI
  const renderStep1 = () => (
    <div className="w-full flex flex-col gap-8">
      <div>
        <div className="text-white text-center font-semibold mb-3">Choose Gender</div>
        <div className="flex gap-4 justify-center">
          {GENDER_OPTIONS.map(opt => {
            const locked = opt.locked && !isSubscriber;
            return (
              <button
                key={opt.value}
                className={`relative w-28 h-32 overflow-hidden rounded-lg select-none flex flex-col items-center justify-center border-2 transition-all bg-zinc-900 ${
                  gender === opt.value ? 'border-pink-500' : 'border-zinc-700'
                }`}
                onClick={() => locked ? setShowUpgradeDialog(true) : handleSelect('gender', opt.value)}
                type="button"
              >
                <span className="text-4xl mb-8">{opt.icon}</span>
                <span className="absolute bottom-1 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-md text-white text-sm font-medium">
                  {opt.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <div className="text-white text-center font-semibold mb-3">Choose Style</div>
        <div className="flex gap-4 justify-center">
          {STYLE_OPTIONS.map(opt => {
            const locked = opt.locked && !isSubscriber;
            return (
              <button
                key={opt.value}
                className={`relative w-52 h-76 overflow-hidden rounded-lg select-none flex flex-col items-center justify-end border-2 transition-all
                  ${style === opt.value ? 'border-pink-500' : 'border-zinc-700'}
                  ${locked ? `${isHidingMonetizationInStores() ? 'hidden' : ''}` : 'hover:border-pink-400'}
                `}
                onClick={() => locked ? setShowUpgradeDialog(true) : handleSelect('style', opt.value)}
                type="button"
              >
                <span className={`h-full w-full ${locked ? 'blur-sm' : ''}`}>
                  <img src={`/images/demo/${gender === 'm' ? opt.mImgPath : opt.imgPath}`} alt={opt.label} className="w-full h-full object-cover" />
                  <span className="absolute bottom-1 left-1/2 -translate-x-1/2 bg-zinc-900/80 px-2 py-0.5 rounded-md text-white text-md font-medium">
                    {opt.label}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  // Step 2 UI
  const renderStep2 = () => (
    <div className="w-full flex flex-col gap-8">
      <div>
        <div className="text-white text-center font-semibold mb-3">Choose Ethnicity</div>
        <div className="flex gap-2 sm:gap-4 justify-center flex-wrap">
          {ETHNICITY_OPTIONS.map(opt => {
            // unlock all options for TWA session
            const locked = (!isHidingMonetizationInStores() && opt.locked) && !isSubscriber;
            return (
              <button
                key={opt.value}
                className={`relative w-25 md:w-28 h-32 rounded-lg overflow-hidden select-none flex flex-col items-center justify-end border-2 transition-all
                  ${ethnicity === opt.value ? 'border-pink-500' : 'border-zinc-700'}
                  ${locked ? 'opacity-50' : 'hover:border-pink-400'}
                `}
                onClick={() => locked ? setShowUpgradeDialog(true) : handleSelect('ethnicity', opt.value)}
                type="button"
              >
                <span className={`h-full w-full ${locked ? 'blur-sm' : ''}`}>
                  <img src={`/images/demo/${gender === 'm' ? opt.mImgPath : opt.imgPath}`} alt={opt.label} className="w-full h-full object-cover" />
                  <span className={`absolute bottom-1 left-1/2 -translate-x-1/2 bg-zinc-900/80 px-1.5 py-0.5 rounded-md text-white text-sm font-medium text-nowrap ${
                    locked ? 'hidden' : ''
                  }`}>
                    {opt.label}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <div className="text-white text-center font-semibold mb-3">Choose Age</div>
        <div className="flex gap-2 sm:gap-4 justify-center flex-wrap">
          {AGE_OPTIONS.map(opt => {
            const locked = opt.locked && !isSubscriber;
            return (
              <button
                key={opt.value}
                className={`relative w-28 h-16 rounded-lg select-none flex flex-col items-center justify-center border-2 transition-all
                  ${age === opt.value ? 'border-pink-500' : 'border-zinc-700'}
                  ${locked ? `opacity-50${isHidingMonetizationInStores() ? ' hidden' : ''}` : 'hover:border-pink-400'}
                `}
                onClick={() => locked ? setShowUpgradeDialog(true) : handleSelect('age', opt.value)}
                type="button"
              >
                <span className={`${locked ? 'blur-sm' : ''}`}>
                  <span className="text-lg font-bold text-white">{opt.label}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  // Step 3 UI
  const renderStep3 = () => (
    <div className="w-full flex flex-col gap-8">
      <div>
        <div className="text-white text-center font-semibold mb-3">Choose Eye Color</div>
        <div className="flex gap-2 sm:gap-4 justify-center flex-wrap">
          {EYE_COLOR_OPTIONS.map(opt => {
            const locked = opt.locked && !isSubscriber;
            return (
              <button
                key={opt.value}
                className={`relative w-25 md:w-28 h-32 rounded-lg overflow-hidden select-none flex flex-col items-center justify-end border-2 transition-all
                  ${state.attributes.eye_color === opt.value ? 'border-pink-500' : 'border-zinc-700'}
                  ${locked ? `${isHidingMonetizationInStores() ? 'hidden' : ''}` : 'hover:border-pink-400'}
                `}
                onClick={() => locked ? setShowUpgradeDialog(true) : handleSelect('eye_color', opt.value)}
                type="button"
              >
                <span className={`h-full w-full ${locked ? 'blur-sm' : ''}`}>
                  <img src={`/images/demo/${gender === 'm' ? opt.mImgPath : opt.imgPath}`} alt={opt.label} className="w-full h-full object-cover" />
                  <span className="absolute bottom-1 left-1/2 -translate-x-1/2 bg-zinc-900/80 px-2 py-0.5 rounded-md text-white font-medium text-sm">
                    {opt.label}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <div className="text-white text-center font-semibold mb-3">Choose Hair Style</div>
        <div className="flex gap-2 sm:gap-4 justify-center flex-wrap">
          {HAIR_STYLE_OPTIONS
            .filter(genderFilter)
            .map(opt => {
            const locked = opt.locked && !isSubscriber;
            return (
              <button
                key={opt.value}
                className={`relative w-25 md:w-28 h-32 rounded-lg overflow-hidden select-none flex flex-col items-center justify-end border-2 transition-all
                  ${state.attributes.hair_style === opt.value ? 'border-pink-500' : 'border-zinc-700'}
                  ${locked ? `${isHidingMonetizationInStores() ? 'hidden' : ''}` : 'hover:border-pink-400'}
                `}
                onClick={() => locked ? setShowUpgradeDialog(true) : handleSelect('hair_style', opt.value)}
                type="button"
              >
                <span className={`h-full w-full ${locked ? 'blur-sm' : ''}`}>
                  <img src={`/images/demo/${gender === 'm' ? opt.mImgPath : opt.imgPath}`} alt={opt.label} className="w-full h-full object-cover" />
                  <span className="absolute bottom-1 left-1/2 -translate-x-1/2 bg-zinc-900/80 px-2 py-0.5 rounded-md text-white font-medium text-sm">
                    {opt.label}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <div className="text-white text-center font-semibold mb-2">Choose Hair Color</div>
        <div className="flex gap-2 sm:gap-4 justify-center flex-wrap">
          {HAIR_COLOR_OPTIONS.filter(genderFilter).map(opt => {
            const locked = opt.locked && !isSubscriber;
            return (
              <button
                key={opt.value}
                className={`relative w-25 md:w-28 h-32 rounded-lg overflow-hidden select-none flex flex-col items-center justify-end border-2 transition-all
                  ${state.attributes.hair_color === opt.value ? 'border-pink-500' : 'border-zinc-700'}
                  ${locked ? `${isHidingMonetizationInStores() ? 'hidden' : ''}` : 'hover:border-pink-400'}
                `}
                onClick={() => locked ? setShowUpgradeDialog(true) : handleSelect('hair_color', opt.value)}
                type="button"
              >
                <span className={`h-full w-full ${locked ? 'blur-sm' : ''}`}>
                  <img src={`/images/demo/${gender === 'm' ? opt.mImgPath : opt.imgPath}`} alt={opt.label} className="w-full h-full object-cover" />
                  <span className="absolute bottom-1 left-1/2 -translate-x-1/2 bg-zinc-900/80 px-2 py-0.5 rounded-md text-white font-medium text-sm">
                    {opt.label}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  // Step 4 UI
  const renderStep4 = () => (
    <div className="w-full flex flex-col gap-8">
      <div>
        <div className="text-white text-center font-semibold mb-3">Choose Body Type</div>
        <div className="flex gap-2 sm:gap-4 justify-center flex-wrap">
          {BODY_TYPE_OPTIONS.filter(genderFilter).map(opt => {
            const locked = opt.locked && !isSubscriber;
            return (
              <button
                key={opt.value}
                className={`relative w-25 md:w-28 h-32 rounded-lg overflow-hidden select-none flex flex-col items-center justify-end border-2 transition-all
                  ${body_type === opt.value ? 'border-pink-500' : 'border-zinc-700'}
                  ${locked ? `${isHidingMonetizationInStores() ? 'hidden' : ''}` : 'hover:border-pink-400'}
                `}
                onClick={() => locked ? setShowUpgradeDialog(true) : handleSelect('body_type', opt.value)}
                type="button"
              >
                <span className={`h-full w-full ${locked ? 'blur-sm' : ''}`}>
                  <img src={`/images/demo/${gender === 'm' ? opt.mImgPath : opt.imgPath}`} alt={opt.label} className="w-full h-full object-cover" />
                  <span className="absolute bottom-1 left-1/2 -translate-x-1/2 bg-zinc-900/80 px-2 py-0.5 rounded-md text-white font-medium text-sm">
                    {opt.label}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
      {!isFromAppStores() && gender === 'f' && (
        <div>
          <div className="text-white text-center font-semibold mb-3">Choose Bust Size</div>
          <div className="flex gap-2 sm:gap-4 justify-center flex-wrap">
            {BUST_SIZE_OPTIONS.map(opt => {
              const locked = opt.locked && !isSubscriber;
              return (
                <button
                  key={opt.value}
                  className={`relative w-25 md:w-28 h-32 rounded-lg overflow-hidden select-none flex flex-col items-center justify-end border-2 transition-all
                    ${bust_size === opt.value ? 'border-pink-500' : 'border-zinc-700'}
                    ${locked ? `${isHidingMonetizationInStores() ? 'hidden' : ''}` : 'hover:border-pink-400'}
                  `}
                  onClick={() => locked ? setShowUpgradeDialog(true) : handleSelect('bust_size', opt.value)}
                  type="button"
                >
                  <span className={`h-full w-full ${locked ? 'blur-sm' : ''}`}>
                    <img src={`/images/demo/${opt.imgPath}`} alt={opt.label} className="w-full h-full object-cover" />
                    <span className="absolute bottom-1 left-1/2 -translate-x-1/2 bg-zinc-900/80 px-2 py-0.5 rounded-md text-white font-medium text-sm">
                      {opt.label}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
      {!isFromAppStores() && gender === 'f' && (
        <div>
          <div className="text-white text-center font-semibold mb-3">Choose Butt Size</div>
          <div className="flex gap-2 sm:gap-4 justify-center flex-wrap">
            {BUTT_SIZE_OPTIONS.map(opt => {
              const locked = opt.locked && !isSubscriber;
              return (
                <button
                  key={opt.value}
                  className={`relative w-25 md:w-28 h-32 rounded-lg overflow-hidden select-none flex flex-col items-center justify-end border-2 transition-all
                    ${butt_size === opt.value ? 'border-pink-500' : 'border-zinc-700'}
                    ${locked ? `${isHidingMonetizationInStores() ? 'hidden' : ''}` : 'hover:border-pink-400'}
                  `}
                  onClick={() => locked ? setShowUpgradeDialog(true) : handleSelect('butt_size', opt.value)}
                  type="button"
                >
                  <span className={`h-full w-full ${locked ? 'blur-sm' : ''}`}>
                    <img src={`/images/demo/${opt.imgPath}`} alt={opt.label} className="w-full h-full object-cover" />
                    <span className="absolute bottom-1 left-1/2 -translate-x-1/2 bg-zinc-900/80 px-2 py-0.5 rounded-md text-white font-medium text-sm">
                      {opt.label}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  // Step 5 UI
  const renderStep5 = () => (
    <div className="w-full flex flex-col gap-8">
      <div>
        <div className="text-white text-center font-semibold mb-3 flex items-center justify-center gap-2">
          <span>Choose Personality</span>
          <button className="ml-2 px-2 py-1 text-xs rounded bg-zinc-700 text-white" type="button" onClick={() => setShowPersonalityCustom(v => !v)}>
            {showPersonalityCustom ? 'Hide' : 'Customize 📝'}
          </button>
        </div>
        {showPersonalityCustom && (
          <textarea
            className="w-full mb-2 p-2 rounded bg-zinc-700 text-white"
            maxLength={1000}
            rows={3}
            value={personalityCustom}
            onChange={e => {
              setPersonalityCustom(e.target.value);
              handleSelect('personality', e.target.value);
            }}
            placeholder="Describe the personality..."
          />
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 justify-center">
          {PERSONALITY_OPTIONS.map(opt => {
            const locked = opt.locked && !isSubscriber;
            return (
              <button
                key={opt.value}
                className={`relative w-full rounded-lg select-none flex flex-col items-center justify-center border-2 transition-all p-4
                  ${personality === opt.value ? 'border-pink-500' : 'border-zinc-700'}
                  ${locked ? `${isHidingMonetizationInStores() ? 'hidden' : ''}` : 'hover:border-pink-400'}
                `}
                onClick={() => locked ? setShowUpgradeDialog(true) : (handleSelect('personality', opt.value), setPersonalityCustom(opt.label))}
                type="button"
              >
                <span className={`text-2xl mb-2 ${locked ? 'blur-md' : ''}`}>{opt.emoji}</span>
                <span className={`font-bold text-white mb-1 ${locked ? 'blur-md' : ''}`}>{opt.label}</span>
                <span className={`text-xs text-zinc-300 text-center ${locked ? 'blur-md' : ''}`}>{opt.desc}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <div className="text-white text-center font-semibold mb-3 flex items-center justify-center gap-2">
          <span>Choose Relationship</span>
          <button className="ml-2 px-2 py-1 text-xs rounded bg-zinc-700 text-white" type="button" onClick={() => setShowRelationshipCustom(v => !v)}>
            {showRelationshipCustom ? 'Hide' : 'Customize 📝'}
          </button>
        </div>
        {showRelationshipCustom &&
          <textarea
            className="w-full mb-2 p-2 rounded bg-zinc-700 text-white"
            maxLength={1000}
            rows={2}
            value={relationshipCustom}
            onChange={e => {
              setRelationshipCustom(e.target.value);
              handleSelect('relationship', e.target.value);
            }}
            placeholder="Describe the relationship..."
          />
        }
        <div className="flex gap-2 sm:gap-4 flex-wrap justify-center">
          {RELATIONSHIP_OPTIONS.filter(genderFilter).filter(appStoreFilter).map(opt => {
            const locked = opt.locked && !isSubscriber;
            return (
              <button
                key={opt.value}
                className={`relative size-25 rounded-lg select-none flex flex-col items-center justify-center border-2 transition-all
                  ${relationship === opt.value ? 'border-pink-500' : 'border-zinc-700'}
                  ${locked ? `${isHidingMonetizationInStores() ? 'hidden' : ''}` : 'hover:border-pink-400'}
                `}
                onClick={() => locked ? setShowUpgradeDialog(true) : (handleSelect('relationship', opt.value), setRelationshipCustom(opt.label))}
                type="button"
              >
                <span className={`text-3xl mb-1 ${locked ? 'blur-md' : ''}`}>{opt.emoji}</span>
                <span className={`font-bold text-white text-sm ${locked ? 'blur-md' : ''}`}>{opt.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  // Step 6 UI
  const renderStep6 = () => (
    <div className="w-full flex flex-col gap-8">
      <div>
        <div className="text-white text-center font-semibold mb-3 flex items-center justify-center gap-2">
          <span>Choose Occupation</span>
          <button className="ml-2 px-2 py-1 text-xs rounded bg-zinc-700 text-white" type="button" onClick={() => setShowOccupationCustom(v => !v)}>
            {showOccupationCustom ? 'Hide' : 'Customize 📝'}
          </button>
        </div>
        {showOccupationCustom &&
          <textarea
            className="w-full mb-2 p-2 rounded bg-zinc-700 text-white"
            maxLength={1000}
            rows={2}
            value={occupationCustom}
            onChange={e => {
              setOccupationCustom(e.target.value);
              handleSelect('occupation', e.target.value);
            }}
            placeholder="Describe the occupation..."
          />
        }
        <div className="flex gap-2 sm:gap-4 flex-wrap justify-center">
          {OCCUPATION_OPTIONS.filter(genderFilter).filter(appStoreFilter).map(opt => {
            const locked = opt.locked && !isSubscriber;
            return (
              <button
                key={opt.value}
                className={`relative w-25 h-14 px-2 rounded-lg select-none flex flex-col items-center justify-center border-2 transition-all
                  ${occupation === opt.value ? 'border-pink-500' : 'border-zinc-700'}
                  ${locked ? `${isHidingMonetizationInStores() ? 'hidden' : ''}` : 'hover:border-pink-400'}
                `}
                onClick={() => locked ? setShowUpgradeDialog(true) : (handleSelect('occupation', opt.value), setOccupationCustom(opt.label))}
                type="button"
              >
                <span className={`font-medium text-white text-sm ${locked ? 'blur-md' : ''}`}>{opt.label}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <div className="text-white text-center font-semibold mb-3 flex items-center justify-center gap-2">
          <span>Choose Hobbies (up to 3)</span>
          <button className="ml-2 px-2 py-1 text-xs rounded bg-zinc-700 text-white" type="button" onClick={() => setShowHobbiesCustom(v => !v)}>
            {showHobbiesCustom ? 'Hide' : 'Customize 📝'}
          </button>
        </div>
        {showHobbiesCustom && (
          <textarea
            className="w-full mb-2 p-2 rounded bg-zinc-700 text-white"
            maxLength={1000}
            rows={2}
            value={hobbiesCustom}
            onChange={e => {
              setHobbiesCustom(e.target.value);
              handleSelect('hobbies', e.target.value.split(',').map(h => h.trim()).filter(Boolean));
            }}
            placeholder="List hobbies, separated by commas..."
          />
        )}
        <div className="flex gap-2 sm:gap-4 flex-wrap justify-center">
          {HOBBY_OPTIONS.map(opt => {
            const locked = opt.locked && !isSubscriber;
            const selected = hobbies.includes(opt.value);
            const disabled = !selected && hobbies.length >= 3;
            return (
              <button
                key={opt.value}
                className={`relative w-24 h-12 px-2 rounded-lg select-none flex flex-col items-center justify-center border-2 transition-all
                  ${selected ? 'border-pink-500' : 'border-zinc-700'}
                  ${locked ? `${isHidingMonetizationInStores() ? 'hidden' : ''}` : !disabled ? 'hover:border-pink-400' : 'opacity-50'}
                `}
                onClick={() => {
                  if (locked) return setShowUpgradeDialog(true);
                  if (selected) {
                    const newHobbies = hobbies.filter((h: string) => h !== opt.value);
                    handleSelect('hobbies', newHobbies);
                    setHobbiesCustom(newHobbies.join(', '));
                  } else if (!disabled) {
                    const newHobbies = [...hobbies, opt.value];
                    handleSelect('hobbies', newHobbies);
                    setHobbiesCustom(newHobbies.join(', '));
                  }
                }}
                type="button"
                disabled={disabled && !selected}
              >
                <span className={`font-medium text-white text-xs ${locked ? 'blur-md' : ''}`}>{opt.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  // Step 7 UI - Outfit & Voice
  const renderStep7 = () => (
    <div className="w-full flex flex-col gap-8">
      <div>
        <div className="text-white text-center font-semibold mb-6 flex items-center justify-center gap-2">
          <span className="text-2xl">Outfit</span>
          <button className="ml-2 px-2 py-1 text-xs rounded bg-zinc-700 text-white" type="button" onClick={() => setShowOutfitCustom(v => !v)}>
            {showOutfitCustom ? 'Hide' : 'Customize 📝'}
          </button>
        </div>
        {showOutfitCustom && (
          <textarea
            className="w-full mb-2 p-2 rounded bg-zinc-700 text-white"
            maxLength={1000}
            rows={2}
            value={outfitCustom}
            onChange={e => {
              setOutfitCustom(e.target.value);
              handleSelect('outfit', e.target.value);
            }}
            placeholder="Describe the outfit..."
          />
        )}
        <div className="flex gap-2 sm:gap-4 flex-wrap justify-center">
          {OUTFIT_OPTIONS.filter(genderFilter).filter(appStoreFilter).map(opt => {
            const locked = opt.locked && !isSubscriber;
            return (
              <button
                key={opt.value}
                className={`relative w-25 h-14 px-2 rounded-lg select-none flex flex-col items-center justify-center border-2 transition-all
                  ${outfit === opt.value ? 'border-pink-500' : 'border-zinc-700'}
                  ${locked ? `${isHidingMonetizationInStores() ? 'hidden' : ''}` : 'hover:border-pink-400'}
                `}
                onClick={() => locked ? setShowUpgradeDialog(true) : (handleSelect('outfit', opt.value), setOutfitCustom(opt.label))}
                type="button"
              >
                <span className={`font-medium text-white text-sm ${locked ? 'blur-md' : ''}`}>{opt.label}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div>
      <div className="text-white text-center font-semibold mb-6 text-2xl">Accent</div>
      <VoiceSelectionGrid
        gender={gender}
        selectedVoice={voice}
        onSelectVoice={(v) => handleSelect('voice', v)}
        isSubscriber={!!isSubscriber}
        onUpgrade={() => setShowUpgradeDialog(true)}
      />
    </div>
    </div>
  );

  // Step 8 UI - Review
  const renderStep8 = () => {
    // Gather all attributes for display
    const summary = [
      { label: 'Gender', value: state.attributes.gender, options: GENDER_OPTIONS },
      { label: 'Style', value: state.attributes.style, options: STYLE_OPTIONS },
      { label: 'Ethnicity', value: state.attributes.ethnicity, options: ETHNICITY_OPTIONS },
      { label: 'Age', value: state.attributes.age, options: AGE_OPTIONS },
      { label: 'Eye Color', value: state.attributes.eye_color, options: EYE_COLOR_OPTIONS },
      { label: 'Hair Style', value: state.attributes.hair_style, options: HAIR_STYLE_OPTIONS },
      { label: 'Hair Color', value: state.attributes.hair_color, options: HAIR_COLOR_OPTIONS },
      { label: 'Body Type', value: state.attributes.body_type, options: BODY_TYPE_OPTIONS },
      { label: 'Bust Size', value: state.attributes.bust_size, options: BUST_SIZE_OPTIONS, show: !isFromAppStores() && state.attributes.gender === 'f' },
      { label: 'Butt Size', value: state.attributes.butt_size, options: BUTT_SIZE_OPTIONS, show: !isFromAppStores() && state.attributes.gender === 'f' },
      { label: 'Voice', value: state.attributes.voice, options: VOICE_OPTIONS },
    ];
    // Helper to get label from value
    const getLabel = (value: string, options: OptionsType[]) => options.find(o => o.value === value)?.label || value;
    const getImgPath = (value: string, options: OptionsType[], gender = 'f') =>
      options.find(o => o.value === value)?.[gender === 'm' ? 'mImgPath' : 'imgPath'] || '';
    // Required fields check
    const required = [
      'gender','style','ethnicity','age','eye_color','hair_style','hair_color','body_type','personality','relationship','occupation','hobbies','outfit','voice',
    ];
    if (state.attributes.gender === 'f') required.push('bust_size', 'butt_size');
    const missing = required.some(k => !state.attributes[k] || (k === 'hobbies' && (!state.attributes.hobbies || state.attributes.hobbies.length === 0)));

    const handleAuthDialogOnClose = (open: boolean, { user, onboardingCompleted }: {
      user?: User,
      onboardingCompleted?: boolean
    } = {}) => {
      setShowAuthDialog(false);
      if (!open && user && onboardingCompleted) handleConfirm();
    }

    const handleMediaRequest = async (customPrompt: string, resourceId: string, { requestId, successfulGenCount = 0 }: {
      requestId?: string,
      successfulGenCount?: number
    } = {}) => {
      if (successfulGenCount >= FREE_PROFILE_IMAGE_LIMIT && !isSubscriber) {
        setShowUpgradeDialog(true);
        return { success: false, userErrorMsg: 'Upgrade to create more profile images' };
      }
      setImageStatus('pending');

      const { data: mediaData, error: mediaError } = await supabase.functions.invoke('eh-media-request', {
        body: {
          customPrompt,
          style: state.attributes.style,
          requestId,
          resource_id: resourceId,
          media_type: 'profile_image',
        }
      });
      if (import.meta.env.DEV) console.log('[DEV] handleMediaRequest data:', mediaData);

      if (mediaError && imageRetryCountRef.current > 0 ) { // retry only once after error
        setImageStatus('failed');
        trackEvent(CHARACTER_EVENTS.NEW_PROFILE_IMAGE_ERROR, {
          error: mediaError,
          url_slug: createdCharacter?.url_slug,
          source: 'CreateCharacterPage',
        });
        return { success: false, userErrorMsg: 'Oops, something went wrong. Please try again.' };
      } else if (mediaError || (mediaData?.retryAfter && imageRetryCountRef.current < 3)) {
        imageRetryCountRef.current++;
        // retryAfter is an epoch timestamp, compute delay in ms
        const delay = mediaData?.retryAfter ? mediaData.retryAfter - Date.now() : 0;
        imageRetryTimeoutRef.current = setTimeout(async () => {
          await handleMediaRequest(customPrompt, resourceId, {
            requestId: mediaData?.requestId || undefined,
            successfulGenCount,
          });
        }, delay);
      } else if (!mediaData?.success || !mediaData?.filePath) {
        setImageStatus('failed');
        trackEvent(CHARACTER_EVENTS.NEW_PROFILE_IMAGE_ERROR, {
          error: `Missing data: ${JSON.stringify(mediaData)}`,
          url_slug: createdCharacter?.url_slug,
          source: 'CreateCharacterPage',
        });
        return { success: false, userErrorMsg: 'Oops, something went wrong. Please retry later.' };
      }

      setAvatarUrl(getMediaUrl(mediaData.filePath));
      setImageStatus('success');
      refetchCharacters();

      storage.setItem(LS_NEW_CHAR_KEY, JSON.stringify({
        ...state, // keep the same state for faster re-creation, but reset the step
        current_step: 0,
      }));
      return { success: true, data: mediaData };
      // setImageRequestId(mediaData?.requestId || null);
    }

    const generatedCharacters = characters?.filter(c => !c.parent_char_id && c.character_scope === 'private') || [];
    const isFirstCharacter = generatedCharacters.length === 0;
    const cost = isFirstCharacter ? 0 : CUSTOM_CHAR_COST;
    const hasEnoughGems = balance.total >= cost;

    const handleConfirm = async () => {
      if (!user) {
        authDialogOnCloseHandlerRef.current = handleAuthDialogOnClose;
        setShowAuthDialog(true);
        return;
      }

      if (!isSubscriber && generatedCharacters.length >= FREE_CHAR_LIMIT && !hasEnoughGems) {
        setShowGemsDialog(true);
        setShowGemBalanceMsg(true);
        // setShowUpgradeDialog(true);
        return;
      }
      if (isSubscriber && !hasEnoughGems) {
        if (showGemBalanceMsg) { // already clicked
          window?.open('/gems', '_blank');
          return;
        }
        setShowGemBalanceMsg(true);
        return;
      }
      setShowGemBalanceMsg(false);

      setCharacterCreationError('');
      setIsSubmitting(true);
      try {
        // Call eh-new-char edge function
        let ethnicity = state.attributes.ethnicity;
        if (ethnicity === 'random') {
          const choices = ETHNICITY_OPTIONS.filter(o => o.value !== 'random');
          ethnicity = choices[Math.floor(Math.random() * choices.length)].value;
          if (import.meta.env.DEV) console.log('[DEV] random ethnicity', ethnicity);
          // Don't modify state: we keep random ethnicity for next generation
          // setState(prev => ({ ...prev, attributes: { ...prev.attributes, ethnicity } }));
        }
        const { data, error } = await supabase.functions.invoke('eh-new-char', {
          body: { attributes: { ...state.attributes, ethnicity, user_id: user.id, safeMode: !is18Confirmed } }
        });
        if (error || !data) {
          setIsSubmitting(false);
          if (import.meta.env.DEV) console.log('[DEV] eh-new-char error', error);
          setCharacterCreationError('Failed to create your AI. Please try again.');
          return;
        }
        if (data.flaggedAttributes && data.flaggedAttributes.length > 0) {
          setIsSubmitting(false);
          setCharacterCreationError('Some attributes include prohibited content: '
            + (data.flaggedAttributes as string[]).map(a => a.replace(/_/g, ' ')).join(', '));
          return;
        }

        await refreshBalance();

        setCreatedCharacter(data.character as Database['eh']['Tables']['characters']['Row']);
        setShowSummaryDialog(true);
        setIsSubmitting(false);
        setImageStatus('pending');
        setAvatarUrl(null);

        // Subscribe to media_requests for status updates if needed
        // const sub = supabase
        //   .channel('eh-media-requests')
        //   .on(
        //     'postgres_changes',
        //     {
        //       event: 'UPDATE',
        //       schema: 'eh',
        //       table: 'media_requests',
        //       filter: `resource_id=eq.${data.character.id}`
        //     },
        //     async (payload: { new: Database['eh']['Tables']['media_requests']['Row'] & { filePath?: string } }) => {
        //       console.log('media_requests update', payload);
        //       const media = payload.new as Database['eh']['Tables']['media_requests']['Row'] & { filePath?: string };
        //       if (media?.media_type === 'profile_image') {
        //         if (media.status === 'success') {
        //           setImageStatus('success');
        //           const { data: charData, error: charError } = await supabaseEh.from('characters')
        //             .select('profile_media_fpaths')
        //             .eq('id', data.character.id)
        //             .single();
        //           if (charData && charData.profile_media_fpaths && charData.profile_media_fpaths.length > 0) {
        //             setAvatarUrl(getMediaUrl(charData.profile_media_fpaths[0]));
        //             storage.setItem(LS_NEW_CHAR_KEY, JSON.stringify({
        //               ...state, // keep the same state for faster re-creation, but reset the step
        //               current_step: 0,
        //             }));
        //           }
        //           if (charError) {
        //             trackEvent(UI_EVENTS.UI_ERROR, {
        //               error: charError,
        //               source: 'CreateCharacterPage',
        //             });
        //           }
        //           sub.unsubscribe();
        //         } else if (media.status === 'failed') {
        //           setImageStatus('failed');
        //           sub.unsubscribe();
        //         } else if (media.status === 'pending') {
        //           setImageStatus('pending');
        //         }
        //       }
        //     }
        //   )
        //   .subscribe();

        await handleMediaRequest(data.imagePrompt, data.character.id);
      } catch (err) {
        setIsSubmitting(false);
        setCharacterCreationError('Failed to create character: ' + (err instanceof Error ? err.message : String(err)));
      } finally {
        // restore default handler
        authDialogOnCloseHandlerRef.current = (open) => { setShowAuthDialog(open); };
      }
    };
    const handleStartChat = () => {
      if (createdCharacter?.url_slug) {
        navigate(`/c/${createdCharacter.url_slug}`);
      } else { // should never happen
        setShowSummaryDialog(false);
        trackEvent(UI_EVENTS.UI_ERROR, {
          error: `Failed to create chat for ${createdCharacter?.id}: No URL slug`,
          source: 'CreateCharacterPage',
        });
      }
    };

    return (
      <div className="w-full flex flex-col gap-8">
        {isSubmitting ? (
          <div className="flex flex-col items-center justify-center">
            <div className="w-16 h-16 border-4 border-zinc-600 border-t-pink-500 rounded-full animate-spin mb-4"></div>
            <p className="text-zinc-300 text-center px-6 text-xl">
              <span>Creating your </span>
              <span className="font-bold">unique AI 🥰</span>
            </p>
          </div>
        ) : (
          <div>
            <div className="text-white text-center font-semibold mb-6 text-2xl">Review & Confirm</div>
            <div className="flex gap-2 sm:gap-4 justify-center flex-wrap gap-4 sm:gap-8 mb-8 place-content-center">
              {summary.filter(s => s.show === undefined || s.show).map(s => (
                <div key={s.label} className="flex flex-col items-center select-none">
                  <div className="text-xs text-zinc-400 mb-1 font-medium">{s.label}</div>
                  <div className="relative w-28 h-32 rounded-lg overflow-hidden bg-zinc-700 flex items-center justify-center text-white text-center">
                    {getImgPath(s.value, s.options, state.attributes.gender) ? (
                       <>
                        <img
                          src={`/images/demo/${getImgPath(s.value, s.options, state.attributes.gender)}`}
                          alt={s.label}
                          className="w-full h-full object-cover"
                        />
                        <span className="absolute bottom-1 left-1/2 -translate-x-1/2 bg-zinc-900/80 px-2 py-0.5 rounded-md text-white text-sm">
                          {getLabel(s.value, s.options)}
                        </span>
                       </>
                    ) : (
                      <span className="text-white font-bold text-lg">{getLabel(s.value, s.options)}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              <div>
                <div className="text-xs text-zinc-400 mb-1">Personality</div>
                <div className="bg-zinc-700 rounded-lg p-2 text-white min-h-[40px]">{
                  getLabel(state.attributes.personality, PERSONALITY_OPTIONS) || state.attributes.personality
                }</div>
              </div>
              <div>
                <div className="text-xs text-zinc-400 mb-1">Relationship</div>
                <div className="bg-zinc-700 rounded-lg p-2 text-white min-h-[40px]">{
                  getLabel(state.attributes.relationship, RELATIONSHIP_OPTIONS) || state.attributes.relationship
                }</div>
              </div>
              <div>
                <div className="text-xs text-zinc-400 mb-1">Occupation</div>
                <div className="bg-zinc-700 rounded-lg p-2 text-white min-h-[40px]">{
                  getLabel(state.attributes.occupation, OCCUPATION_OPTIONS) || state.attributes.occupation
                }</div>
              </div>
              <div>
                <div className="text-xs text-zinc-400 mb-1">Hobbies</div>
                <div className="bg-zinc-700 rounded-lg p-2 text-white min-h-[40px]">{
                  Array.isArray(state.attributes.hobbies) ? state.attributes.hobbies.join(', ') : state.attributes.hobbies
                }</div>
              </div>
              <div>
                <div className="text-xs text-zinc-400 mb-1">Outfit</div>
                <div className="bg-zinc-700 rounded-lg p-2 text-white min-h-[40px]">{
                  getLabel(state.attributes.outfit, OUTFIT_OPTIONS) || state.attributes.outfit
                }</div>
              </div>
              <div>
                <div className="text-xs text-zinc-400 mb-1">Voice</div>
                <div className="bg-zinc-700 rounded-lg p-2 text-white min-h-[40px]">{
                  getLabel(state.attributes.voice, VOICE_OPTIONS) || state.attributes.voice
                }</div>
              </div>
            </div>
            <div className="flex flex-col items-center gap-4 mt-6">
              {characterCreationError && (
                <div className="text-red-500 text-center">{characterCreationError}</div>
              )}
              
              {/* Gem cost information */}
              {/* {user && (
                (() => {
                  if (cost > 0 && !hasEnoughGems && showGemBalanceMsg) {
                    if (import.meta.env.DEV) console.log(`[DEV] balance: ${balance.total} 💎, cost: ${cost} 💎`);
                    return (
                      <div className={`text-center px-4 py-2 rounded ${hasEnoughGems ? 'bg-zinc-700' : 'bg-rose-900'}`}>
                        <div className="text-sm text-zinc-300">
                          <span>Character creation costs </span>
                          <span className="font-semibold">{`${cost} 💎`}</span>
                          <span>{`, you have ${balance.total} 💎`}</span>
                        </div>
                        {!hasEnoughGems && (
                          <div className="text-red-400 text-sm mt-1">
                            <span className="text-blue-400 cursor-pointer underline" onClick={() => setShowUpgradeDialog(true)}>
                              Get more gems
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  } else if (cost > 0 || isLoadingUserInfo) {
                    return null;
                  }
                  
                  return (
                    <div className="text-center px-4 py-2 rounded bg-green-900">
                      <div className="text-sm text-green-300">
                        <span>🎉 First character is free!</span>
                      </div>
                    </div>
                  );
                })()
              )} */}

              <button
                className="px-8 py-3 rounded bg-pink-600 text-white font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleConfirm}
                disabled={missing || isSubmitting}
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    <span>Creating</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center">
                    <div className="flex items-center justify-center   gap-2">
                      <span>Confirm & Create</span>
                      {(cost > 0 && !isLoadingUserInfo) && (
                        <span className="text-sm bg-pink-700/80 rounded-md px-2 py-1">{`${cost} 💎`}</span>
                      )}
                    </div>
                    {!isLoadingUserInfo && (
                      <div className="text-xs">
                        {cost === 0 ? (<span>First character is free!</span>) : (!hasEnoughGems && showGemBalanceMsg) && (
                          <span
                            tabIndex={0}
                            role="button"
                            className="underline">
                            Get more gems
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </button>
              {!hasEnoughGems && generatedCharacters.length && (
                <Button variant="link" asChild className="text-xs -mt-4">
                  <Link to={`/c/${generatedCharacters[0].url_slug}`}>
                    <ArrowRight className="size-3" />
                    <span>or chat with your free character</span>
                  </Link>
                </Button>
              )}
            </div>
          </div>
        )}
        {createdCharacter && (
          <CharacterCreationSuccessDialog
            open={showSummaryDialog}
            onOpenChange={(open) => {
              setShowSummaryDialog(open);
              if (!open) goToStep(0)
            }}
            character={createdCharacter}
            imageStatus={imageStatus}
            avatarUrl={avatarUrl}
            onStartChat={handleStartChat}
            onRetryImage={handleMediaRequest}
            onCharacterSave={() => {
              refetchCharacters();
            }}
            userAttributes={state.attributes}
          />
        )}
      </div>
    );
  };

  // Set defaults for each step on mount/step change
  useEffect(() => {
    const setDefault = (key: string, options: OptionsType[]) => {
      const availableOptions = options.filter(genderFilter);
      if (!state.attributes[key]) {
        const def = availableOptions.find(o => o.isDefault)
          || availableOptions[Math.floor(Math.random() * availableOptions.length)];
        if (def) {
          handleSelect(key, def.value);
          return def;
        };
      }
    };
    if (state.current_step === 0) {
      setDefault('gender', GENDER_OPTIONS);
      setDefault('style', STYLE_OPTIONS);
    } else if (state.current_step === 1) {
      setDefault('ethnicity', ETHNICITY_OPTIONS);
      setDefault('age', AGE_OPTIONS);
    } else if (state.current_step === 2) {
      setDefault('eye_color', EYE_COLOR_OPTIONS);
      setDefault('hair_style', HAIR_STYLE_OPTIONS);
      setDefault('hair_color', HAIR_COLOR_OPTIONS);
    } else if (state.current_step === 3) {
      setDefault('body_type', BODY_TYPE_OPTIONS);
      if (state.attributes.gender === 'f') setDefault('bust_size', BUST_SIZE_OPTIONS);
      if (state.attributes.gender === 'f') setDefault('butt_size', BUTT_SIZE_OPTIONS);
    } else if (state.current_step === 4) {
      const defPers = setDefault('personality', PERSONALITY_OPTIONS);
      const defRel = setDefault('relationship', RELATIONSHIP_OPTIONS);
      if (!personalityCustom) setPersonalityCustom(defPers ? defPers.label : '');
      if (!relationshipCustom) setRelationshipCustom(defRel ? defRel.label : '');
    } else if (state.current_step === 5) {
      const defOcc = setDefault('occupation', OCCUPATION_OPTIONS);
      if (!occupationCustom) setOccupationCustom(defOcc ? defOcc.label : '');
      const defHobbies = HOBBY_OPTIONS.filter(o => o.isDefault).map(o => o.value);
      if ((!state.attributes.hobbies || state.attributes.hobbies.length === 0) && defHobbies.length > 0) handleSelect('hobbies', defHobbies);
      if (!hobbiesCustom) setHobbiesCustom(defHobbies.join(', '));
    } else if (state.current_step === 6) {
      const defOutfit = setDefault('outfit', OUTFIT_OPTIONS);
      if (!outfitCustom) setOutfitCustom(defOutfit ? defOutfit.label : '');
      setDefault('voice', VOICE_OPTIONS);
    }
    // Add similar logic for future steps
    // eslint-disable-next-line
  }, [genderFilter, state.current_step, state.attributes.gender]);


  // Card UI shell
  return (
    <div className="flex flex-col">
      <SEO
        title="Create Your Own AI Girlfriend or companion - EveningHoney.AI"
        description="Create your own AI girlfriend, wife, colleage, or dreamy companion with EveningHoney.AI. Customize every detail to create the perfect AI to chat with."
        ogUrl="/create-character"
      />
      <div className="flex-1 flex flex-col items-center justify-center bg-zinc-900 py-4 sm:py-8 px-1">
        <h1 ref={titleRef} className={`text-2xl sm:text-3xl font-bold text-white mb-2 ${isSubmitting ? 'invisible' : ''}`}>
          Create your own AI
        </h1>
        {renderTimeline()}
        <div className="w-full max-w-2xl bg-zinc-800 rounded-lg shadow-lg mt-2 pt-4 px-4 sm:px-8 pb-8 flex flex-col items-center">
          {state.current_step < 7 && ( // single choice in step 8 (review)
            <h2 className="text-xl sm:text-2xl font-semibold text-white mb-4">{stepTitles[state.current_step]}</h2>
          )}
          {/* Step content */}
          <div className="w-full flex flex-col items-center justify-center min-h-[300px]">
            {state.current_step === 0 ? renderStep1() :
              state.current_step === 1 ? renderStep2() :
              state.current_step === 2 ? renderStep3() :
              state.current_step === 3 ? renderStep4() :
              state.current_step === 4 ? renderStep5() :
              state.current_step === 5 ? renderStep6() :
              state.current_step === 6 ? renderStep7() :
              state.current_step === 7 ? renderStep8() : (
              <span className="text-zinc-400">Step {state.current_step + 1} content goes here.</span>
            )}
          </div>
          <div className={`flex w-full justify-between mt-8 ${isSubmitting ? 'hidden' : ''}`}>
            <button
              className="px-4 py-2 rounded bg-zinc-700 text-white disabled:opacity-50 select-none"
              onClick={prevStep}
              disabled={state.current_step === 0}
            >
              Previous
            </button>
            <button
              className={`px-4 py-2 rounded bg-pink-600 text-white disabled:opacity-50 select-none ${state.current_step === 7 ? 'hidden' : ''}`}
              onClick={nextStep}
              disabled={
                state.current_step === TOTAL_STEPS - 1 ||
                (state.current_step === 0 && (!gender || !style)) ||
                (state.current_step === 1 && (!ethnicity || !age)) ||
                (state.current_step === 2 && (!state.attributes.eye_color || !state.attributes.hair_style || !state.attributes.hair_color)) ||
                (state.current_step === 3 && (!body_type || (gender === 'f' && !(bust_size && butt_size)))) ||
                (state.current_step === 4 && (!state.attributes.personality || !state.attributes.relationship)) ||
                (state.current_step === 5 && (!state.attributes.occupation || !state.attributes.hobbies/* || state.attributes.hobbies.length === 0*/)) ||
                (state.current_step === 6 && (!state.attributes.outfit || !state.attributes.voice))
              }
            >
              Next
            </button>
          </div>
        </div>
      </div>
      <EhAuthDialog
          open={showAuthDialog}
          onOpenChange={authDialogOnCloseHandlerRef.current}
          onOnboardingOpenChange={authDialogOnCloseHandlerRef.current}
        />
      <EhUpgradeDialog
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
        limitType="create"
        onUpgradeClick={() => {
          if (!user) {
            setShowAuthDialog(true);
            return false;
          }
          return true;
        }}
      />
      <EhGemsDialog
        open={showGemsDialog}
        onOpenChange={setShowGemsDialog}
        action={'character'}
      />
    </div>
  );
} 